import type { PdfDocumentType, TenantName, WatermarkConfig } from '../@types/types';
import { ResourceState } from '../domain/schemas/contract.schema';

const WATERMARK_ENABLED_STATUSES: ReadonlySet<ResourceState> = new Set([
  ResourceState.DRAFT,
  ResourceState.REVIEW,
  ResourceState.ARCHIVED,
]);

const DEFAULT_WATERMARK_DOCUMENT_TYPES: PdfDocumentType[] = [
  'submission-document',
  'submission-comparison-document',
  'endorsement-summary',
];

export const pdfWatermarkDocumentTypesByClient: Record<TenantName, PdfDocumentType[]> = {
  'ct-lockton': [],
  'ct-bms': ['submission-document', 'submission-comparison-document'],
  artificial: DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'smart-placement': DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'ct-aon': DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'ct-ajg': DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'ct-howden': DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'ct-mcgill': DEFAULT_WATERMARK_DOCUMENT_TYPES,
  'ct-ppl': DEFAULT_WATERMARK_DOCUMENT_TYPES,
};

const DEFAULT_WATERMARK_STYLE: WatermarkConfig = {
  type: 'body',
  classNames: ['text-info-200', 'font-bold', 'uppercase', 'fixed', 'z-[-1]'],
  cssText:
    'left: 50%; top: 50%; transform: translate(calc(-50% + 20px), -50%) rotate(-45deg); font-size: 160px; line-height: 194px; font-family: "Gotham Bold"',
  innerHTML: 'draft',
};

export const pdfWatermarkStyleByClient: Record<TenantName, WatermarkConfig> = {
  'ct-bms': {
    type: 'header',
    classNames: ['text-info-200', 'font-bold', 'uppercase', 'fixed', 'z-[-1]'],
    cssStyleProps: {
      left: '50%',
      top: '34px',
      transform: 'translateX(-50%)',
      fontSize: '24px',
      fontFamily: '"Gotham Bold"',
    },
    innerHTML: 'quotation',
  },
  'ct-lockton': {
    type: 'body',
    classNames: ['text-info-200', 'font-bold', 'uppercase', 'fixed', 'z-[-1]'],
    cssText:
      'left: 50%; top: 50%; transform: translate(calc(-50% + 20px), -50%) rotate(-45deg); font-size: 160px; line-height: 194px; font-family: "Gotham Bold"',
    innerHTML: 'quote',
  },
  'ct-howden': {
    type: 'body',
    classNames: ['text-info-200', 'font-bold', 'uppercase', 'fixed', 'z-[-1]'],
    cssText:
      'left: 50%; top: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 128px; line-height: 155px; font-family: "Gotham Bold"',
    innerHTML: 'quotation',
  },
  artificial: DEFAULT_WATERMARK_STYLE,
  'smart-placement': DEFAULT_WATERMARK_STYLE,
  'ct-aon': DEFAULT_WATERMARK_STYLE,
  'ct-ajg': DEFAULT_WATERMARK_STYLE,
  'ct-mcgill': DEFAULT_WATERMARK_STYLE,
  'ct-ppl': DEFAULT_WATERMARK_STYLE,
};

export const pdfTenantsWatermarkConfig = ({
  client,
  status,
  pdfDocumentType,
}: {
  client: TenantName;
  status?: ResourceState;
  pdfDocumentType?: PdfDocumentType;
}): WatermarkConfig | undefined => {
  const watermarkDocTypes = pdfWatermarkDocumentTypesByClient[client] ?? DEFAULT_WATERMARK_DOCUMENT_TYPES;

  const isWatermarkEnabled =
    !!status &&
    !!pdfDocumentType &&
    WATERMARK_ENABLED_STATUSES.has(status) &&
    watermarkDocTypes.includes(pdfDocumentType);

  return isWatermarkEnabled ? (pdfWatermarkStyleByClient[client] ?? DEFAULT_WATERMARK_STYLE) : undefined;
};
